import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { LoggerService } from "@bfx/logger";
import { Checkbox } from "@bfx/primeng/checkbox";
import { TranslateService } from "@ngx-translate/core";
import { BfxAutoCompleteComponent } from "app/bfx-element-library/bfx-autocomplete.component";
import {
  ICAutocompleteGenericModel,
  ICMarkupModel,
} from "app/order-entry/interface/controls/models/bfx-interface-controls.models";
import {
  DEFAULT_CHANGE_CALLBACK,
  DEFAULT_TOUCH_CALLBACK,
  OnChangeCallbackType,
  OnTouchCallbackType,
} from "shared/base-components/models/ControlValueAccessorTypes";
import { NumberSpinnerComponent } from "shared/base-components/number-spinner.component";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { MarkupCriteria, MarkupPromiseService } from "shared/cubiq-services";
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
import { OENLockedState } from "../../data/enums/oe-locked-state.enum";
import { ICNInputControlState } from "./models/bfx-interface-controls.enums";
import { BfxInputDirective } from "../../../../shared/base-components/bfx-input";
import { SizeMixin } from "shared/base-components/mixins/size";
import { EmphasisMixin } from "shared/base-components/mixins/emphasis";
import { InputLabelStateMixin } from "shared/base-components/mixins/label-state";
import { DisabledMixin } from "shared/base-components/mixins/disabled";
import { ComponentMixin } from "shared/base-components/mixins/mixin";
const logger = LoggerService.for(__dirname);

export const MARKUP_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxMarkupComponent),
  multi: true,
};

const pips: ICAutocompleteGenericModel = new ICAutocompleteGenericModel(
  CubiqTS.MarkUpType.Pips,
  "Pips",
);
const percent: ICAutocompleteGenericModel = new ICAutocompleteGenericModel(
  CubiqTS.MarkUpType.Percentage,
  "%",
);
const dollerPm: ICAutocompleteGenericModel = new ICAutocompleteGenericModel(
  CubiqTS.MarkUpType.UsdPerMillion,
  "$/m",
);
const MARKUP_TYPE_DATASET: Array<ICAutocompleteGenericModel> = [pips, percent, dollerPm];

const Mixins = SizeMixin(EmphasisMixin(InputLabelStateMixin(DisabledMixin(ComponentMixin))));
@Component({
  selector: "bfx-markup",
  templateUrl: "./html/bfx-markup.html",
  providers: [MARKUP_VALUE_ACCESSOR],
  standalone: false,
})
export class BfxMarkupComponent
  extends Mixins
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  /* INPUTs */
  @Input() inputId: string;

  @Input("disabled") input_disabled: boolean;

  @Input("label") input_label: string;

  @Input("pair") pair: CubiqTS.CurrencyPairImpl;

  @Input("isFwd") isFwd: boolean;

  @Input("trigger") trigger: CubiqTS.RequestTrackingType;

  @Input("isInvert") isInvert: boolean;

  @Input() isAmendMarkup: boolean = false;

  @Input() showValidationBar?: boolean = true;

  @Input("markupCriteria") input_markupCriteria: MarkupCriteria;

  @Input("retrieveEnabled") input_retrieveMarkupEnabled: boolean;
  @Input("hideTrackingRate") hideTrackingRate: boolean = false;

  @Input() entryType: OENEntryType;

  @Input("markupType") get markupType() {
    return this.data.markup.typeValue;
  }
  set markupType(value: ICAutocompleteGenericModel) {
    if (this.data.markup.typeValue != value) {
      this.data.markup.typeValue = value;
      this.onSelectMarkupType(value);
    }
  }

  @Input() get trackingRate() {
    return this.data.trackingRate;
  }

  set trackingRate(value: any) {
    this.data.trackingRate = value;
    if (this.entryType === OENEntryType.BENCHMARK) {
      this.data.trackingData = "";
      this.data.trackingTooltip = "";
    } else {
      this.data.trackingData = `${this.data.trackingLabel}${
        this.data.trackingRate !== "" ? ": " : " "
      }${this.data.trackingRate}`;
      this.data.trackingTooltip = `${this.data.trackingTooltipLabel}${
        this.data.trackingRate !== "" ? ": " : " "
      }${this.data.trackingRate}`;
    }

    this.updateTrackingStyle();
  }

  @Input() get clientRate() {
    return this.data.clientRate;
  }

  set clientRate(value: any) {
    this.data.clientRate = value;
    if (this.entryType === OENEntryType.BENCHMARK) {
      this.data.clientData = "";
      this.data.clientTooltip = "";
    } else {
      this.data.clientData = `${this.data.clientLabel}${this.data.clientRate !== "" ? ": " : " "}${
        this.data.clientRate
      }`;
      this.data.clientTooltip = `${this.data.clientTooltipLabel}${
        this.data.clientRate !== "" ? ": " : " "
      }${this.data.clientRate}`;
    }

    this.updateTrackingStyle();
  }

  /* OUTPUTs */
  @Output() blur: EventEmitter<any> = new EventEmitter<any>();

  @Output() focus: EventEmitter<any> = new EventEmitter<any>();

  @Output() onGetMarkupCriteria: EventEmitter<any> = new EventEmitter<any>();

  @Output() onMarkupCheckboxState: EventEmitter<boolean> = new EventEmitter<boolean>();

  /* INPUT <--> OUTPUT */
  /* External Validation */
  input_externalState: ICNInputControlState;

  @Input() get externalState() {
    return this.input_externalState;
  }

  set externalState(value: any) {
    if (this.input_externalState != value) {
      this.input_externalState = value;
    }
  }

  /* HTML ELEMENTs */
  @ViewChild("inputMarkupType", { static: true })
  markupTypeInterface: BfxAutoCompleteComponent;

  @ViewChild("chkBox", { static: true }) markupCheckboxInterface: Checkbox;

  @ViewChild("markup_spinner", { static: true }) markupSpinner: NumberSpinnerComponent;

  /** ControlValueAccessor callback */
  public onModelChange: OnChangeCallbackType = DEFAULT_CHANGE_CALLBACK;
  /** ControlValueAccessor callback */
  public onModelTouched: OnTouchCallbackType = DEFAULT_TOUCH_CALLBACK;

  /***************************************/
  /* DATA */
  public data: {
    trackingTooltipLabel: string;
    clientTooltipLabel: string;
    markup: ICMarkupModel; // ICMarkupModel
    //
    label: string;
    disabled: boolean;
    precision: number;
    step: CubiqTS.BigNumber;
    min: CubiqTS.BigNumber;
    max: CubiqTS.BigNumber;
    maxLength: number;
    emptyValue: string;
    retrieveMarkupEnabled: boolean;
    showRefresh: boolean;
    trackingLabel: string;
    trackingRate: string;
    trackingData: string;
    trackingTooltip: string;
    clientLabel: string;
    clientRate: string;
    clientData: string;
    clientTooltip: string;
    filteredTypeDataset: Array<ICAutocompleteGenericModel>;

    state: ICNInputControlState;
    checkbox: {
      lockState: OENLockedState;
    };
    trackingEmphasized: boolean;
  } = {
    markup: new ICMarkupModel(), // ICMarkupModel
    //
    label: "Markup",
    disabled: false,
    precision: null,
    step: new CubiqTS.BigNumber(1),
    min: new CubiqTS.BigNumber(-9999999),
    max: new CubiqTS.BigNumber(9999999),
    maxLength: 7,
    emptyValue: "",
    retrieveMarkupEnabled: false,
    showRefresh: false,
    trackingLabel: "",
    trackingTooltipLabel: "",
    trackingRate: "",
    trackingData: "",
    trackingTooltip: "",
    clientLabel: "",
    clientTooltipLabel: "",
    clientRate: "",
    clientData: "",
    clientTooltip: "",
    filteredTypeDataset: [],

    state: ICNInputControlState.INACTIVE,

    checkbox: {
      lockState: OENLockedState.UNLOCKED,
    },
    trackingEmphasized: false,
  };

  public inputFocused: boolean = false;
  public locked: boolean = false;

  // Log debug control (to be removed in the future)
  private outputLog: boolean = false;
  private useConsole: boolean = false;
  private useLogger: boolean = false;

  /**********************************************************************/
  /* CONSTRUCTOR + HOOKS */

  constructor(
    injector: Injector,
    element: ElementRef,
    private markupService: MarkupPromiseService,
    public translationService: TranslateService,
    public inputDirective: BfxInputDirective,
    public cd: ChangeDetectorRef,
  ) {
    super(injector);
    this.size = "sm";
    this.data.filteredTypeDataset = MARKUP_TYPE_DATASET.concat();
  }

  ngOnInit() {
    this.initialize();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  ngOnDestroy() {}

  ngOnChanges(changes: SimpleChanges) {
    //this.initialize();
    for (let propName in changes) {
      this.logDebugInfo(
        `ngOnChanges() -> propertyName: ${propName} | value: ${
          typeof this[propName] == "string" ? this[propName] : ""
        }`,
        typeof this[propName] == "object" ? this[propName] : null,
        true,
      );
      switch (propName) {
        case "input_markupCriteria":
          if (this.input_markupCriteria != null && !this.data.markup.checkbox.value) {
            this.markupCriteriaUpdate(this.input_markupCriteria);
          }
          break;
        case "input_label":
          if (typeof this.input_label === "string") {
            this.data.label = this.input_label;
          }
          break;
        case "externalState":
          if (this.input_externalState != null) {
            this.data.state = this.input_externalState;
          }
          break;
        case "input_retrieveMarkupEnabled":
          if (
            this.input_retrieveMarkupEnabled == null ||
            typeof this.input_retrieveMarkupEnabled != "boolean"
          ) {
            this.data.retrieveMarkupEnabled = false;
          } else {
            this.data.retrieveMarkupEnabled = this.input_retrieveMarkupEnabled;
          }
          break;
        case "pair":
          if (this.pair instanceof CubiqTS.CurrencyPairImpl) {
            this.updateMarkupPrecision();
          }
          break;
        case "inputId":
        case "input_disabled":
        case "trackingRate":
          // do nothing
          break;

        default:
          this.logDebugInfo(`ngOnChanges() -> propertyName: ${propName} = UNHANDLED `, null, true);
          break;
      }
    }
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }

  /*******************************/
  /* INITIALIZATION */

  protected initialize(): void {
    try {
      this.data.label = this.input_label;
      this.data.trackingLabel = this.translationService.instant(
        "order_entry.panel.label.markup.tracking.rate",
      );
      this.data.clientLabel = this.translationService.instant(
        "order_entry.panel.label.markup.client.rate",
      );
      this.data.trackingTooltipLabel = this.translationService.instant(
        "order_entry.panel.tooltip.label.markup.tracking.rate",
      );
      this.data.clientTooltipLabel = this.translationService.instant(
        "order_entry.panel.tooltip.label.markup.client.rate",
      );
      let precision = this.markupService.getMarkupPrecisionByType(
        this.pair,
        this.isInvert,
        this.markupType?.value,
      );
      this.logDebugInfo(
        `initialize() -> Precision New: ${precision} | Old: ${this.data.precision}`,
        null,
        true,
      );
      if (precision !== null) {
        // if precision is changed assign and update the render
        this.data.precision = precision;
        if (this.markupType.value === CubiqTS.MarkUpType.Percentage) {
          this.data.step = new CubiqTS.BigNumber(
            new CubiqTS.BigNumber(10).pow(-this.data.precision),
          );
        } else {
          this.data.step = new CubiqTS.BigNumber(1);
        }
        this.onChange();
      }

      // retrieve markup fg
      /*    if(this.input_retrieveMarkupEnabled == null || typeof this.input_retrieveMarkupEnabled != 'boolean'){
                this.data.retrieveMarkupEnabled = false;
            }else{
                this.data.retrieveMarkupEnabled =  this.input_retrieveMarkupEnabled;
            } */
    } catch (ex) {
      logger.out.error(`ERROR: _icMarkup.initialize(): exception:`, ex);
    }
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    if (value != null) {
      this.data.markup = value;
    } else {
      this.data.markup.value = null;
      this.data.markup.textValue = null;
      this.data.markup.typeValue = null;
    }
    this.markupSpinner.loadValue(this.data.markup.value);
  }

  public registerOnChange(fn: OnChangeCallbackType): void {
    this.onModelChange = fn;
  }

  public registerOnTouched(fn: OnTouchCallbackType): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* PUBLIC API METHODS */

  public resetInputControl(): void {
    this.logDebugInfo(`resetInputControl() markup = `, this.data.markup, true);
    this.data.markup.value = null;
    this.data.markup.textValue = "";
    this.data.markup.typeValue = null;
    this.data.disabled = false;
    this.markupType = null;
    this.data.retrieveMarkupEnabled = this.input_retrieveMarkupEnabled;
    this.externalState = null;
    this.data.state = ICNInputControlState.INACTIVE;

    // checkbox
    this.data.markup.checkbox.value = false;
    this.data.checkbox.lockState = OENLockedState.UNLOCKED;
  }

  public lockInputControl(): void {
    this.setLocked(true);
    this.data.markup.checkbox.enabled = true;
  }

  public unlockInputControl(): void {
    this.setLocked(false);
  }

  private setLocked(value: boolean) {
    this.locked = value;
    this.disabled = value;
    this.inputLabelState = value ? "disabled" : "enabled";
  }

  public load(markup: ICMarkupModel) {
    /*  this.logDebugInfo(`load() -> Markup new = `, markup, true);
        this.logDebugInfo(`load() -> Markup old = `, this.data.markup, true); */
    if (markup instanceof ICMarkupModel || markup === null) {
      this.data.markup = markup;
      if (this.markupSpinner) {
        this.markupSpinner.loadValue(this.data.markup.value);
      }
      if (markup != null && markup.typeValue instanceof ICAutocompleteGenericModel) {
        this.markupType = markup.typeValue;
        this.updateMarkupPrecision();
        this.onChange();
      }
      this.validate();
    }
  }

  public onFocus($event: any): void {
    this.inputFocused = true;
    this.validate();
    $event.target.select();
    this.focus.emit($event);
  }

  public onBlur($event): void {
    this.inputFocused = false;
    this.validate();
    this.onModelTouched();
    // this.onChange(null);
    // this.updateValue(this.data.markup.value);
    this.blur.emit($event);
  }

  public onTypeFocus(_event): void {
    this.data.state = ICNInputControlState.ACTIVE;
  }

  /**
   * Need to ensure label is white when spinner has focus
   * @param event
   */
  public onStepperFocus(): void {
    if (!this.inputFocused) {
      this.inputFocused = true;
      this.validate();
      this.focus.emit();
    }
  }

  public onStepperBlur(): void {
    this.inputFocused = false;
    this.onModelTouched();
    this.validate();
    this.blur.emit();
  }

  public onTypeBlur($event): void {
    this.data.markup.typeValue = this.markupType;
    this.onSelectMarkupType(this.markupType);
    this.updateValue(this.markupType);
    this.onModelTouched();
    this.validate();
    // this.onChange(null);
    this.blur.emit($event);
  }

  public onChange(): void {
    if (!this.markupSpinner) {
      return;
    }
    const newValue = this.markupSpinner.bigNumberValue;
    const textValue = newValue ? newValue.toString() : "<NULL>";
    this.logDebugInfo(
      `[START] onChange() -> Markup Value = New: ${textValue} | Old: ${
        this.data.markup != null ? this.data.markup.value : "NULL"
      }`,
      null,
      true,
    );
    this.updateValue(newValue);
    this.validate();
    this.updateTrackingStyle();
    this.logDebugInfo(
      `[END] onChange() -> Value = ${
        this.data.markup != null && this.data.markup.value instanceof CubiqTS.BigNumber
          ? this.data.markup.value.toString()
          : "NULL"
      }` +
        ` | TextValue = ${
          this.data.markup != null && this.data.markup.textValue != null
            ? this.data.markup.textValue
            : "NULL"
        }` +
        ` | Checkbox Enabled = ${
          this.data.markup != null &&
          this.data.markup.checkbox != null &&
          typeof this.data.markup.checkbox.enabled == "boolean"
            ? this.data.markup.checkbox.enabled
              ? "YES"
              : "NO"
            : "NULL"
        }` +
        ` | Checkbox Value = ${
          this.data.markup != null &&
          this.data.markup.checkbox != null &&
          typeof this.data.markup.checkbox.value == "boolean"
            ? this.data.markup.checkbox.value
              ? "YES"
              : "NO"
            : "NULL"
        }`,
      null,
      true,
    );
  }

  /**********************************************************************/
  /* INTERNAL METHODS */

  public displayMarkupRefresh(show: boolean): void {
    this.data.showRefresh = show;
    this.cd.markForCheck();
  }

  /**********************************************************************/
  /* INTERNAL METHODS */

  private updateValue(value: any): void {
    this.logDebugInfo(
      `[START] updateValue() -> Markup Value = New: ${value} | Old: ${
        this.data.markup != null ? this.data.markup.value : "NULL"
      }`,
      null,
      true,
    );
    if (!(value instanceof ICMarkupModel) && this.data.markup == null) {
      this.data.markup = new ICMarkupModel();
    }
    if (value == null) {
      this.data.markup.value = null;
      this.data.markup.textValue = null;
      // this.data.markup.checkbox.value = false;
    } else if (value instanceof ICMarkupModel) {
      this.data.markup = value;
    } else if (value instanceof CubiqTS.BigNumber) {
      this.data.markup.value = value;
      this.data.markup.textValue = value.toFixed(this.data.precision);
    } else if (value instanceof ICAutocompleteGenericModel) {
      this.data.markup.typeValue = value;
    } else if (typeof value === "boolean") {
      this.data.markup.checkbox.value = value;
    }
    this.onModelChange(this.data.markup);
    this.logDebugInfo(
      `[END] updateValue() -> Value = ${
        this.data.markup != null && this.data.markup.value instanceof CubiqTS.BigNumber
          ? this.data.markup.value.toString()
          : "NULL"
      }` +
        ` | TextValue = ${
          this.data.markup != null && this.data.markup.textValue != null
            ? this.data.markup.textValue
            : "NULL"
        }` +
        ` | Checkbox Enabled = ${
          this.data.markup != null &&
          this.data.markup.checkbox != null &&
          typeof this.data.markup.checkbox.enabled == "boolean"
            ? this.data.markup.checkbox.enabled
              ? "YES"
              : "NO"
            : "NULL"
        }` +
        ` | Checkbox Value = ${
          this.data.markup != null &&
          this.data.markup.checkbox != null &&
          typeof this.data.markup.checkbox.value == "boolean"
            ? this.data.markup.checkbox.value
              ? "YES"
              : "NO"
            : "NULL"
        }`,
      null,
      true,
    );
  }
  /**********************************************************************/
  /* CHECKBOX */

  public onMarkupCheckboxChange(_$event): void {
    if (this.data.markup.checkbox.value) {
      setTimeout(() => this.markupSpinner.focusInput(), 0);
    } else {
      this.data.state = ICNInputControlState.INACTIVE;
    }
    this.updateValue(this.data.markup.checkbox.value);
    this.validate();
    this.onMarkupCheckboxState.emit(this.data.markup.checkbox.value);
    // this.onModelChange(this.data.markup);
  }

  /**********************************************************************/
  /* VALIDATION */

  public validateWithDelay(offset: number = 0): void {
    setTimeout(() => {
      this.validate();
      this.blur.emit(event);
    }, offset);
  }

  public validate(): void {
    this.logDebugInfo(`[START] validate() -> Initialize`, null, true);
    // adjust
    if (this.data.markup == null) {
      this.data.markup = new ICMarkupModel();
    }
    //
    if (this.data.markup.checkbox.value === true) {
      // component is interactive (focused)
      if (this.inputFocused) {
        /// user is interacting with the component
        this.data.state = ICNInputControlState.ACTIVE;
      } else {
        // actual validation

        // VALUE VALIDATION
        let valueValidity: ICNInputControlState;
        if (
          this.data.markup.textValue === this.data.emptyValue ||
          this.data.markup.textValue == null
        ) {
          // post initialization (no value)
          valueValidity = ICNInputControlState.INACTIVE;
        } else {
          if (
            this.data.markup.value !== null &&
            this.data.markup.value.isGreaterThanOrEqualTo(this.data.min) &&
            this.data.markup.value.isLessThanOrEqualTo(this.data.max)
          ) {
            // valid criteria
            valueValidity = ICNInputControlState.VALID;
          } else {
            valueValidity = ICNInputControlState.INVALID;
          }
        }

        // TYPE VALIDATION
        let typeValidity: ICNInputControlState;
        if (this.markupType == null && valueValidity != ICNInputControlState.INACTIVE) {
          typeValidity = ICNInputControlState.INVALID;
        } else if (this.markupType != null) {
          let typeLabel =
            this.markupType instanceof ICAutocompleteGenericModel
              ? this.markupType.label.toLowerCase()
              : typeof this.markupType === "string"
                ? this.markupType
                : null;
          let markupType: ICAutocompleteGenericModel = MARKUP_TYPE_DATASET.find(
            (markupTypeItem: ICAutocompleteGenericModel) =>
              markupTypeItem.label.toLowerCase() == typeLabel,
          );

          if (markupType instanceof ICAutocompleteGenericModel) {
            typeValidity = ICNInputControlState.VALID;
          } else {
            typeValidity = ICNInputControlState.INVALID;
          }
        }

        // The reckoning
        if (
          valueValidity === ICNInputControlState.INVALID ||
          typeValidity === ICNInputControlState.INVALID
        ) {
          this.data.state = ICNInputControlState.INVALID;
        } else if (
          valueValidity === ICNInputControlState.VALID &&
          typeValidity === ICNInputControlState.VALID
        ) {
          this.data.state = ICNInputControlState.VALID;
        } else {
        }
      }
    } else {
      // component is not interactive
      this.data.state = ICNInputControlState.INACTIVE;
      this.externalState = null;
    }

    this.logDebugInfo(
      `[END] validate() -> Validation result: ${ICNInputControlState[this.data.state]}`,
      null,
      true,
    );
  }

  /**********************************************************************/
  /* MARKUP CRITERIA */

  public markupCriteriaUpdate(criteria: MarkupCriteria): void {
    if (criteria != null && criteria instanceof MarkupCriteria) {
      this.markupService.retrieveMarkupForCriteria(criteria).then(
        (markup) => {
          if (!(markup instanceof CubiqTS.MarkupInfoImpl)) {
            this.updateMarkupPrecision();
            if (this.markupSpinner) {
              this.markupSpinner.loadValue(0);
            }
            return;
          }

          if (markup.markupType === CubiqTS.MarkUpType.Decimal) {
            markup.markupType = CubiqTS.MarkUpType.Pips;
            markup.markupQty = markup.markupQty.times(
              new CubiqTS.BigNumber(10).pow(
                this.markupService.getSpotPrecision(this.pair, this.isInvert),
              ),
            );
          }
          this.markupType = new ICAutocompleteGenericModel(
            markup.markupType,
            this.markupTypeToLabel(markup.markupType),
          );
          this.updateMarkupPrecision();
          this.onSelectMarkupType(this.markupType);
          if (this.markupSpinner) {
            const markupQty = CubiqTS.BigNumber.isBigNumber(markup.markupQty)
              ? markup.markupQty
              : 0;
            this.markupSpinner.loadValue(markupQty);
          }
        },
        (err) => logger.out.error("bfx-markup-component = PROMISE ERROR:", err),
      );
    }
  }

  public retrieveMarkup($event: any) {
    this.onGetMarkupCriteria.emit($event);
    this.validateWithDelay(100);
  }

  /************************************************************/
  /* MARKUP TYPE */

  /**
   * Searches the Triggers suggestions for a infix matching of the given filter string
   * @param filter the filter should be checked to be an infix of the target trader group
   */
  public lookupMarkupType(filter: any): void {
    let query: string = filter.query.toLowerCase();
    if (query.length > 0) {
      this.data.filteredTypeDataset = MARKUP_TYPE_DATASET.filter((markupType: any) =>
        markupType.label.toLowerCase().includes(query),
      );
    } else {
      this.data.filteredTypeDataset = MARKUP_TYPE_DATASET.slice(0);
    }
  }

  public onSelectMarkupType(markupType: any): void {
    if (typeof markupType === "string") {
      let markupTypeObject: ICAutocompleteGenericModel = MARKUP_TYPE_DATASET.find(
        (markupTypeItem: ICAutocompleteGenericModel) =>
          markupTypeItem.label.toLowerCase() == markupType,
      );
      if (markupTypeObject instanceof ICAutocompleteGenericModel) {
        this.markupType = markupTypeObject;
      }
    }

    this.updateMarkupPrecision();
    this.validate();
    this.onChange();
  }

  private markupTypeToLabel(type: CubiqTS.MarkUpType): string {
    switch (type) {
      case CubiqTS.MarkUpType.Percentage:
        return "%";
      case CubiqTS.MarkUpType.UsdPerMillion:
        return "$/m";
      case CubiqTS.MarkUpType.Pips:
        return "Pips";
    }
    return "Decimals";
  }

  /************************************************************/
  /* MARKUP PRECISION */
  private updateMarkupPrecision(): void {
    let precision = this.markupService.getMarkupPrecisionByType(
      this.pair,
      this.isInvert,
      this.markupType?.value,
    );
    this.logDebugInfo(
      `[START] updateMarkupPrecision() -> Precision = New: ${precision} | Old: ${this.data.precision}`,
      null,
      true,
    );
    if (this.data.precision !== precision && precision != null) {
      // if precision is changed assign and update the render
      this.data.precision = precision;
      if (this.data.markup.value != null && this.data.markup.value instanceof CubiqTS.BigNumber) {
      }
      if (this.markupType !== null && this.markupType.value === CubiqTS.MarkUpType.Percentage) {
        this.data.step = new CubiqTS.BigNumber(new CubiqTS.BigNumber(10).pow(-this.data.precision));
      } else {
        this.data.step = new CubiqTS.BigNumber(1);
      }
      // this.onChange(this.data.markup.value.toFixed(this.data.precision));
    }
    this.logDebugInfo(
      `[END] updateMarkupPrecision() -> Precision Result: ${this.data.precision}`,
      null,
      true,
    );
  }

  /* ******************************************************* */
  /*  STYLING - TRACKING */
  private updateTrackingStyle(): void {
    this.data.trackingEmphasized =
      this.data != null &&
      typeof this.data.trackingRate === "string" &&
      this.data.trackingRate.length > 1 &&
      this.data.markup != null &&
      this.data.markup.value instanceof CubiqTS.BigNumber &&
      this.data.markup.value.isGreaterThan(0);
  }

  /* ******************************************************* */
  /*  DEBUG - LOG */

  protected logDebugInfo(
    message: string,
    dumpData: any = null,
    warning: boolean = false,
    error: boolean = false,
    feature: string = "MARKUP Comp.",
  ): void {
    try {
      if (this.outputLog) {
        // && this.orderEntryExtendedLogging){
        let prefix = `${
          error ? "ERROR: " : warning ? "WARN: " : "INFO: "
        }${feature} ${Date.now()} -> `;
        if (error) {
          if (dumpData != null) {
            if (this.useLogger)
              logger.out.error(
                `${prefix} ${typeof message === "string" ? message : ""} %O`,
                dumpData,
              );
            if (this.useConsole)
              logger.out.error(`${prefix} ${typeof message === "string" ? message : ""}`, dumpData);
          } else {
            if (this.useLogger)
              logger.out.error(`${prefix} ${typeof message === "string" ? message : ""}`);
            if (this.useConsole)
              logger.out.error(`${prefix} ${typeof message === "string" ? message : ""}`);
          }
        } else if (warning) {
          if (dumpData != null) {
            if (this.useLogger)
              logger.out.warn(
                `${prefix} ${typeof message === "string" ? message : ""} %O`,
                dumpData,
              );
            if (this.useConsole)
              logger.out.warn(`${prefix} ${typeof message === "string" ? message : ""}`, dumpData);
          } else {
            if (this.useLogger)
              logger.out.warn(`${prefix} ${typeof message === "string" ? message : ""}`);
            if (this.useConsole)
              logger.out.warn(`${prefix} ${typeof message === "string" ? message : ""}`);
          }
        } else {
          if (dumpData != null) {
            if (this.useLogger)
              logger.out.info(
                `${prefix} ${typeof message === "string" ? message : ""} %O`,
                dumpData,
              );
            if (this.useConsole)
              logger.out.info(`${prefix} ${typeof message === "string" ? message : ""}`, dumpData);
          } else {
            if (this.useLogger)
              logger.out.info(`${prefix} ${typeof message === "string" ? message : ""}`);
            if (this.useConsole)
              logger.out.info(`${prefix} ${typeof message === "string" ? message : ""}`);
          }
        }
      }
    } catch (ex) {
      logger.out.error(`WORKER-ERROR: Extended logging exception (Other): %O`, ex);
    }
  }
}
